import type { Provider } from '@/types/tenant';

let inMemoryProviderSlug: string | null = null;

const STORAGE_KEY = 'florentin.provider.domain';
const SLUG_STORAGE_KEY = 'florentin.provider.slug';

interface DomainComputationOptions {
  provider?: Provider | null;
  fallbackSlug?: string | null;
  fallbackHostname?: string;
}

export function computeProviderDomain({
  provider,
  fallbackSlug,
  fallbackHostname,
}: DomainComputationOptions): string | null {
  if (provider?.domain && provider.domain.length > 0) {
    return provider.domain;
  }

  const suffix = import.meta.env.VITE_PROVIDER_DOMAIN_SUFFIX as string | undefined;
  const slug = fallbackSlug ?? provider?.slug ?? null;

  if (suffix && slug) {
    return `${slug}.${suffix}`;
  }

  if (fallbackHostname) {
    return fallbackHostname;
  }

  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }

  return null;
}

export function setProviderDomain(domain: string | null) {
  if (typeof window === 'undefined') {
    return;
  }

  if (domain) {
    window.localStorage.setItem(STORAGE_KEY, domain);
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

export function getProviderDomain(): string | null {
  const envOverride = import.meta.env.VITE_PROVIDER_DOMAIN_HEADER as string | undefined;
  if (envOverride && envOverride.trim().length) {
    return envOverride.trim();
  }

  if (typeof window !== 'undefined' && window.location.hostname) {
    return window.location.hostname;
  }

  return null;
}

export function setProviderSlug(slug: string | null) {
  if (!slug) {
    inMemoryProviderSlug = null;
  } else {
    inMemoryProviderSlug = slug;
  }

  if (typeof window === 'undefined') {
    return;
  }

  if (slug) {
    window.localStorage.setItem(SLUG_STORAGE_KEY, slug);
  } else {
    window.localStorage.removeItem(SLUG_STORAGE_KEY);
  }
}

export function getProviderSlug(): string | null {
  if (inMemoryProviderSlug) {
    return inMemoryProviderSlug;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem(SLUG_STORAGE_KEY);
  if (stored) {
    inMemoryProviderSlug = stored;
    return stored;
  }

  return null;
}
